import { useState, useEffect, useRef } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Icon } from '../ui';
import { NotesFeed } from './Notes';
import { DealTasksPanel } from './DealTasksPanel';
import { formatInstant } from '../../utils/format';

// ─── Combined Notes & Tasks panel ────────────────────────────────────────────
// One tab that folds together a deal / deal-detail's notes and its tasks. It
// leads with a single Claude summary that now covers BOTH — the notes AND the
// tasks (each with its own notes + conversation) — then offers "+ Add Task" and
// "Write a note…" and shows both lists.
//
// The summary lives server-side (GET /api/notes/summary), keyed on the notes
// scope but now built from notes + tasks (see api/src/functions/noteSummaries.js).
// It never blocks on Claude: a stale/missing summary is regenerated off the
// request path, so we poll until it's fresh and let either child notify us
// (onChanged) when a write should refresh it.

const fmtStamp = (utcStr) => {
  if (!utcStr) return '';
  return formatInstant(utcStr);
};

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5, margin: '4px 0' }}>
    {children}
  </div>
);

export const NotesTasksPanel = ({ dealId, dealDetailId, users = [], includeDealLevel = false, targetNoteId = null, onNoteTargetHandled }) => {
  const params = dealDetailId
    ? `dealId=${dealId}&dealDetailId=${dealDetailId}${includeDealLevel ? '&includeDealLevel=true' : ''}`
    : `dealId=${dealId}`;

  const { data: summary, reload: reloadSummary } = useApi(
    () => api.get(`notes/summary?${params}`),
    [dealId, dealDetailId, includeDealLevel]
  );

  const [refreshing, setRefreshing] = useState(false);

  // Poll while the summary is (re)generating. Capped so a stuck/absent worker
  // (or missing ANTHROPIC_API_KEY) doesn't poll forever. Mirrors NotesFeed.
  const pollRef = useRef(0);
  useEffect(() => {
    const pending = summary && (summary.stale || summary.status === 'pending');
    if (!pending) { pollRef.current = 0; return undefined; }
    if (pollRef.current >= 24) return undefined;  // ~2 min at 5s intervals
    const t = setTimeout(() => { pollRef.current += 1; reloadSummary(); }, 5000);
    return () => clearTimeout(t);
  }, [summary, reloadSummary]);

  // A note/task write happened in a child — recheck the summary (the read path
  // enqueues a rebuild if the notes+tasks signature moved) and start polling.
  const onChildChanged = () => { pollRef.current = 0; reloadSummary(); };

  // Force a rebuild (server enqueues a fresh regeneration), then poll.
  const refreshSummary = async () => {
    setRefreshing(true);
    try { await api.get(`notes/summary?${params}&refresh=1`); } catch { /* best-effort */ }
    pollRef.current = 0;
    reloadSummary();
    setRefreshing(false);
  };

  const updating = !!summary && (summary.stale || summary.status === 'pending');
  const hasActivity = !!summary && (summary.noteCount > 0 || !!summary.content);

  return (
    <div style={{ marginTop: 8 }}>
      {/* The .note-content styles used by the summary HTML are injected by the
          NotesFeed rendered below (its <style> block is always mounted). */}

      {/* Combined summary — notes + tasks + each task's notes/conversation. */}
      {hasActivity && (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, background: B.white, padding: '12px 14px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5 }}>Summary</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {updating ? (
                <span style={{ fontSize: 11, color: B.textLt, fontStyle: 'italic' }}>Updating…</span>
              ) : summary?.generatedUTC ? (
                <span style={{ fontSize: 11, color: B.textLt }}>Updated {fmtStamp(summary.generatedUTC)}</span>
              ) : null}
              <button
                onClick={refreshSummary}
                disabled={refreshing || updating}
                title="Refresh summary"
                aria-label="Regenerate summary"
                style={{ background: 'none', border: 'none', cursor: (refreshing || updating) ? 'default' : 'pointer', color: B.textMd, lineHeight: 1, padding: 2, opacity: (refreshing || updating) ? 0.5 : 1, display: 'inline-flex' }}
              ><Icon name="refresh" size={14} /></button>
            </div>
          </div>
          {summary?.content ? (
            <div className="note-content" style={{ fontSize: 13, color: B.text, lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: summary.content }} />
          ) : summary?.status === 'error' ? (
            <div style={{ fontSize: 12, color: B.textLt }}>
              Summary couldn&apos;t be generated. Use the refresh button to try again.
            </div>
          ) : (
            <div style={{ fontSize: 12, color: B.textLt, fontStyle: 'italic' }}>Generating summary…</div>
          )}
        </div>
      )}

      {/* Tasks — its own "+ Add Task" button and list. */}
      <SectionLabel>Tasks</SectionLabel>
      <DealTasksPanel
        dealId={dealId}
        dealDetailId={dealDetailId}
        users={users}
        includeDealLevel={includeDealLevel}
        onChanged={onChildChanged}
      />

      {/* Notes — its own "Write a note…" composer and list. Summary suppressed
          here (this panel owns the combined one above). */}
      <div style={{ marginTop: 24 }}>
        <SectionLabel>Notes</SectionLabel>
        <NotesFeed
          dealId={dealId}
          dealDetailId={dealDetailId}
          users={users}
          includeDealLevel={includeDealLevel}
          showSummary={false}
          onChanged={onChildChanged}
          targetNoteId={targetNoteId}
          onNoteTargetHandled={onNoteTargetHandled}
        />
      </div>
    </div>
  );
};
